import { Button } from "@ths/ui";
import type { FormEvent, Ref } from "react";

import type { ReminderOptions } from "../components/ReminderConsentFields";
import { ReminderSettings } from "../components/ReminderSettings";
import type { SelfCheckinLookupReservation } from "../generated/graphql";

import { formatShortDisplayDate } from "./dateFormatting";

type ArrivalStay = Pick<
  SelfCheckinLookupReservation,
  "basecampName" | "checkInDate" | "checkOutDate"
>;

export function ArrivalStaySummary({ stay }: { stay: ArrivalStay }) {
  return (
    <div className="space-y-1 text-center">
      <p className="break-words font-oswald text-lg font-medium uppercase tracking-wide">
        {stay.basecampName}
      </p>
      <p className="text-sm text-on-navy/70">
        Arrive {formatShortDisplayDate(stay.checkInDate)} · Depart{" "}
        {stay.checkOutDate ? formatShortDisplayDate(stay.checkOutDate) : "Open"}
      </p>
    </div>
  );
}

interface CheckInStepProps {
  headingRef: Ref<HTMLHeadingElement>;
  stay?: ArrivalStay;
  vehicle: string;
  collectReminders: boolean;
  reminderOptions?: ReminderOptions;
  reminderOptionsLoading?: boolean;
  onRetryReminderOptions?: () => void;
  reminderPromptSeen: boolean;
  onReminderPromptSeen: () => void;
  initialSmsReminderStatus?: string | null;
  smsConsent: boolean;
  onSmsConsentChange: (value: boolean) => void;
  error: string;
  canSubmit: boolean;
  onSubmit: (event: FormEvent) => void;
  onBack: () => void;
}

export default function CheckInStep({
  headingRef,
  stay,
  vehicle,
  collectReminders,
  reminderOptions,
  reminderOptionsLoading,
  onRetryReminderOptions,
  reminderPromptSeen,
  onReminderPromptSeen,
  initialSmsReminderStatus,
  smsConsent,
  onSmsConsentChange,
  error,
  canSubmit,
  onSubmit,
  onBack,
}: CheckInStepProps) {
  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="bc-fade-up w-full max-w-sm space-y-8 text-on-navy"
    >
      <header className="space-y-6 text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="font-oswald text-3xl font-medium uppercase tracking-wide focus:outline-none sm:text-4xl"
        >
          Ready to check in?
        </h1>
        {stay && <ArrivalStaySummary stay={stay} />}
        <p className="break-words text-sm text-on-navy/70">{vehicle}</p>
      </header>

      {collectReminders && (
        <>
          {!reminderOptions && (
            <div className="space-y-2 text-sm text-on-navy/70">
              <p role="status">
                {reminderOptionsLoading
                  ? "Loading text reminder options…"
                  : "Text reminders could not be loaded. You can still check in."}
              </p>
              {!reminderOptionsLoading && onRetryReminderOptions && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onRetryReminderOptions}
                  className="text-on-navy hover:bg-on-navy/10 hover:text-on-navy"
                >
                  Retry
                </Button>
              )}
            </div>
          )}
          {reminderOptions?.smsAvailable &&
            !["ENABLED", "BLOCKED", "DISMISSED"].includes(
              initialSmsReminderStatus ?? "",
            ) && (
              <ReminderSettings
                options={reminderOptions}
                promptOnMount={!reminderPromptSeen}
                onPromptHandled={onReminderPromptSeen}
                smsEnabled={smsConsent}
                onSave={(sms) => onSmsConsentChange(sms)}
              />
            )}
        </>
      )}

      {error && (
        <p
          role="alert"
          className="rounded-xl border border-error/40 bg-error/10 px-4 py-3 text-body text-error-surface"
        >
          {error}
        </p>
      )}
      <Button
        type="submit"
        variant="ghost"
        size="lg"
        disabled={!canSubmit}
        className={`h-auto min-h-12 w-full rounded-xl px-8 py-4 font-oswald text-lg font-medium uppercase tracking-wider focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong active:scale-[0.98] disabled:opacity-100 ${canSubmit ? "bg-highlight text-primaryDarkBlue shadow-lg shadow-highlight/20 hover:bg-highlight" : "bg-on-navy/[0.06] text-on-navy/50 hover:bg-on-navy/[0.06]"}`}
      >
        Check in
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={onBack}
        className="h-auto w-full py-3 text-sm font-medium text-on-navy/25 hover:bg-transparent hover:text-on-navy/40 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
      >
        Go Back
      </Button>
    </form>
  );
}
